import React, { useEffect, useMemo, useRef, useState } from 'react';

// Caps the textarea's auto-grow height at roughly six lines of text; beyond
// that the textarea scrolls internally instead of pushing the rest of the
// chat layout off-screen.
const MAX_TEXTAREA_HEIGHT_PX = 160;

// How many mention candidates to show at once; the member/agent list for a
// channel is small, but this keeps the popup from growing unbounded.
const MAX_MENTION_SUGGESTIONS = 8;

// Figures out whether the caret currently sits inside an "@mention" token: an
// '@' that starts at the beginning of the text or right after whitespace, with
// no whitespace typed between it and the caret. Returns the token's start
// index and what has been typed after the '@' so far, or null if the caret
// isn't inside one.
const detectMention = (value, caret) => {
  const beforeCaret = value.slice(0, caret);
  const atIndex = beforeCaret.lastIndexOf('@');
  if (atIndex === -1) return null;

  const charBeforeAt = atIndex > 0 ? beforeCaret[atIndex - 1] : null;
  if (charBeforeAt !== null && !/\s/.test(charBeforeAt)) return null;

  const query = beforeCaret.slice(atIndex + 1);
  if (/\s/.test(query)) return null;

  return { start: atIndex, query };
};

const MessageForm = ({
  onSend,
  disabled,
  members = [],
  agents = [],
  placeholder = 'Type a message...',
}) => {
  const [body, setBody] = useState('');
  // The active mention token being typed: { start, query }, or null when the
  // popup is closed.
  const [mention, setMention] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);
  const formRef = useRef(null);
  // Caret position to restore after a mention is inserted; applied in the
  // effect below once the textarea has re-rendered with the new value.
  const pendingCaretRef = useRef(null);

  // Same people shown in the channel's member sidebar (MemberList): online
  // users and the Claude agents added to the channel.
  const mentionCandidates = useMemo(
    () => [
      ...members.map((member) => ({ id: member.id, name: member.displayName, isAgent: false })),
      ...agents.map((agent) => ({ id: agent.id, name: agent.name, isAgent: true })),
    ],
    [members, agents]
  );

  const suggestions = useMemo(() => {
    if (!mention) return [];
    const query = mention.query.toLowerCase();
    return mentionCandidates
      .filter((candidate) => candidate.name.toLowerCase().includes(query))
      .slice(0, MAX_MENTION_SUGGESTIONS);
  }, [mention, mentionCandidates]);

  // Reset the highlighted suggestion whenever the mention query changes.
  const mentionQuery = mention ? mention.query : null;
  useEffect(() => {
    setSelectedIndex(0);
  }, [mentionQuery]);

  // Close the mention popup when clicking outside the form.
  useEffect(() => {
    if (!mention) return undefined;
    const onDocClick = (e) => {
      if (formRef.current && !formRef.current.contains(e.target)) {
        setMention(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [mention]);

  // Auto-resize the textarea to fit its content, up to MAX_TEXTAREA_HEIGHT_PX.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT_PX);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > MAX_TEXTAREA_HEIGHT_PX ? 'auto' : 'hidden';
  }, [body]);

  // Restores the caret position after a mention is inserted: setBody() only
  // takes effect on the next render, so the caret can't be repositioned until
  // the textarea has that new value.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || pendingCaretRef.current == null) return;
    const pos = pendingCaretRef.current;
    pendingCaretRef.current = null;
    textarea.setSelectionRange(pos, pos);
  }, [body]);

  const submit = () => {
    if (!body.trim()) return;
    onSend(body);
    setBody('');
    setMention(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  const handleChange = (e) => {
    const { value, selectionStart } = e.target;
    setBody(value);
    setMention(detectMention(value, selectionStart));
  };

  // Replaces the "@query" token with "@name " (trailing space) and closes the
  // popup, then puts the caret right after the inserted text.
  const confirmMention = (candidate) => {
    if (!mention) return;
    const before = body.slice(0, mention.start);
    const after = body.slice(mention.start + 1 + mention.query.length);
    const insertion = `@${candidate.name} `;
    pendingCaretRef.current = before.length + insertion.length;
    setBody(`${before}${insertion}${after}`);
    setMention(null);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    // While the mention popup is open, arrow keys move the highlighted
    // suggestion and Enter/Tab confirm it instead of moving the caret or
    // sending the message.
    if (mention && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        confirmMention(suggestions[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMention(null);
        return;
      }
    }

    // Enter sends the message; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div ref={formRef}>
      <form className="gh-chat-form" onSubmit={handleSubmit}>
        <div className="gh-chat-form-input">
          {mention && suggestions.length > 0 && (
            <ul className="dropdown-menu show gh-mention-menu">
              {suggestions.map((candidate, index) => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    className={`dropdown-item d-flex align-items-center${index === selectedIndex ? ' active' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onClick={() => confirmMention(candidate)}
                  >
                    <i
                      className={`bi ${candidate.isAgent ? 'bi-robot' : 'bi-person-fill'} me-2`}
                      aria-hidden="true"
                    ></i>
                    {candidate.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <textarea
            ref={textareaRef}
            className="form-control gh-chat-form-textarea"
            rows={1}
            placeholder={placeholder}
            value={body}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-label="Message"
          />
        </div>
        <button
          type="submit"
          className="btn btn-gh-primary gh-chat-form-send"
          disabled={disabled || !body.trim()}
          aria-label="Send"
        >
          <i className="bi bi-send" aria-hidden="true"></i>
        </button>
      </form>
      <div className="gh-chat-form-hint text-muted-gh small">Shift+Enter for a new line</div>
    </div>
  );
};

export default MessageForm;
